/**
 * Friends, one way: the people you added (from the leaderboard or their friend link) and, on
 * `/friends/added-you`, the people who added you. Nobody is asked; adding back is up to you.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type AddedYou as AddedYouRow, type Friend } from '../lib/api';
import { AuthGate } from '../components/AuthGate';
import { Toast } from '../kit/Toast';

export function Friends() {
  return (
    <AuthGate title="Friends">
      <FriendsInner />
    </AuthGate>
  );
}

const linkFor = (code: string) => `${location.origin}/friends/add/${code}`;

function FriendsInner() {
  const [code, setCode] = useState<string | null>(null);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [addedYou, setAddedYou] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.getFriends();
      setCode(r.code);
      setFriends(r.friends);
      setAddedYou(r.addedYou.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your friends.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function copy() {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(linkFor(code));
      setNotice('Link copied.');
    } catch {
      setNotice('Copy the link from the box.');
    }
  }

  async function newLink() {
    if (!confirm('Make a new link? Anyone holding the old one can no longer use it.')) return;
    try {
      setCode((await api.newFriendCode()).code);
      setNotice('New link made.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not make a new link.');
    }
  }

  async function remove(friend: Friend) {
    if (!confirm(`Remove ${friend.displayName} from your friends?`)) return;
    try {
      await api.removeFriend(friend.id);
      setFriends((list) => list.filter((f) => f.id !== friend.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove that friend.');
    }
  }

  return (
    <div className="centered">
      <div className="col" style={{ width: 'min(34rem, 100%)', gap: '0.75rem' }}>
        <div className="card col">
          <h1>Friends</h1>
          <p className="muted" style={{ marginTop: 0 }}>
            Add people from the leaderboard, or send your link: whoever opens it adds you. Your friends
            are the people you added, and you can compare scores with them on the leaderboard.
          </p>
          {code ? (
            <div className="field">
              <label htmlFor="friend-link">Your friend link</label>
              <div className="row" style={{ gap: '0.4rem' }}>
                <input
                  id="friend-link"
                  readOnly
                  value={linkFor(code)}
                  onFocus={(e) => e.target.select()}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <button className="btn btn-primary" type="button" onClick={copy}>
                  Copy
                </button>
              </div>
              <button className="btn btn-ghost btn-sm" type="button" onClick={newLink} style={{ alignSelf: 'start' }}>
                Make a new link
              </button>
            </div>
          ) : null}
          {error ? <p className="error-text">{error}</p> : null}

          <Link className="btn btn-ghost" to="/friends/added-you" style={{ alignSelf: 'start' }}>
            Who added you{addedYou > 0 ? ` (${String(addedYou)})` : ''}
          </Link>

          <h2>Your friends</h2>
          {friends.length === 0 ? (
            <p className="faint">No friends yet.</p>
          ) : (
            <ul className="friend-list">
              {friends.map((f) => (
                <li key={f.id}>
                  <span>{f.displayName}</span>
                  <button className="btn btn-ghost btn-sm" type="button" onClick={() => remove(f)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Link className="btn btn-ghost" to="/leaderboard">
          Leaderboard
        </Link>
      </div>
      <Toast message={notice} onDismiss={() => setNotice(null)} />
    </div>
  );
}

/** Someone's friend link, opened. Adding waits for a click, so a link preview never befriends anyone. */
export function AddFriend() {
  return (
    <AuthGate title="Add a friend">
      <AddFriendInner />
    </AuthGate>
  );
}

function AddFriendInner() {
  const { code = '' } = useParams();
  const [added, setAdded] = useState<Friend | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add() {
    setBusy(true);
    setError(null);
    try {
      setAdded((await api.addFriend(code)).friend);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that friend.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="centered">
      <div className="card col" style={{ width: 'min(30rem, 100%)' }}>
        <h1>Add a friend</h1>
        {added ? (
          <>
            <p>{added.displayName} is now your friend.</p>
            <Link className="btn btn-primary" to="/friends">
              See your friends
            </Link>
          </>
        ) : (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              Someone sent you their friend link. Add them to compare scores on the leaderboard; they’ll see
              that you added them.
            </p>
            {error ? <p className="error-text">{error}</p> : null}
            <button className="btn btn-primary btn-lg" type="button" onClick={add} disabled={busy}>
              {busy ? 'Adding…' : 'Add friend'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** Who added you. Adding someone grants them nothing here, so this list only offers Add back. */
export function AddedYou() {
  return (
    <AuthGate title="Who added you">
      <AddedYouInner />
    </AuthGate>
  );
}

function AddedYouInner() {
  const [people, setPeople] = useState<AddedYouRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getFriends()
      .then((r) => setPeople(r.addedYou))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not load the list.'));
  }, []);

  async function addBack(person: AddedYouRow) {
    setError(null);
    try {
      await api.addFriendById(person.id);
      setPeople((list) => list && list.map((p) => (p.id === person.id ? { ...p, added: true } : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add them back.');
    }
  }

  return (
    <div className="centered">
      <div className="col" style={{ width: 'min(34rem, 100%)', gap: '0.75rem' }}>
        <div className="card col">
          <h1>Who added you</h1>
          <p className="muted" style={{ marginTop: 0 }}>
            People who added you as a friend. Add them back to see them on your friends leaderboard.
          </p>
          {error ? <p className="error-text">{error}</p> : null}
          {people && people.length === 0 ? <p className="faint">Nobody has added you yet.</p> : null}
          {people && people.length > 0 ? (
            <ul className="friend-list">
              {people.map((p) => (
                <li key={p.id}>
                  <span>{p.displayName}</span>
                  {p.added ? (
                    <span className="faint">Your friend</span>
                  ) : (
                    <button
                      className="btn btn-sm"
                      type="button"
                      aria-label={`Add ${p.displayName} back`}
                      onClick={() => void addBack(p)}
                    >
                      Add back
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <Link className="btn btn-ghost" to="/friends">
          ← Friends
        </Link>
      </div>
    </div>
  );
}
